import React, { useRef, useState } from 'react';
import { Menu } from 'primereact/menu';
import { confirmDialog } from 'primereact/confirmdialog';
import { RenameColumnDialog } from './';
import { ManageColumn } from '../Stage';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { formatStageName } from 'utils/utils';

const ColumnHeader = ({
  column,
  itemsCount,
  totalItems,
  isLocked,
  handleRef,
  isDefaultColumn,
  onRename,
  onDelete,
  onDuplicate,
  openAddTask,
}) => {
  const opanel = useRef();
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showManageColumn, setShowManageColumn] = useState(false);
  const { addToast } = useToast();

  const handleRename = () => {
    setShowRenameDialog(true);
  };

  const confirmRename = async (newName) => {
    if (newName && newName.trim() !== column?.task_stage_name) {
      const result = await onRename({ sk: column.sk, pk: column.pk, newName: newName.trim(), task_stage_id: column.task_stage_id });
      if (!result?.success) {
        console.error('Failed to rename column');
      }
    }
  };

  const handleDelete = () => {
    confirmDialog({
      message:
        itemsCount > 0
          ? `This stage contains tasks. Deleting the stage will also delete the associated tasks. Are you sure you want to proceed with the deletion?`
          : `Are you sure you want to delete "${column?.task_stage_name} stage?`,
      header: 'Confirm Delete',
      icon: 'pi pi-info-circle',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',
      acceptClassName: 'p-button-danger',
      style: { width: '450px' },
      rejectClassName: 'outline',
      accept: async () => {
        const result = await onDelete(column.task_stage_id, column.pk);
        if (!result?.success) {
          console.error('Failed to archive column');
        }
      },
      reject: () => {},
    });
  };

  const handleManageColumn = () => {
    setShowManageColumn(true);
  };

  const handleDuplicate = async () => {
    if (!column) return;

    const result = await onDuplicate(column);

    if (result?.success) {
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TASK_STAGE_CREATED);
    } else if (result?.locked) {
      addToast(
        false,
        toastConstant.toasterType.WARNING,
        toastConstant.api.WARNING,
        'This stage is currently being duplicated. Please wait.'
      );
    } else {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, result?.error?.message || 'Failed to duplicate stage');
    }
  };

  const menuItems = [
    {
      label: 'Manage Column',
      icon: 'pi pi-cog',
      command: handleManageColumn,
      disabled: false,
    },
    {
      label: 'Rename',
      icon: 'pi pi-pencil',
      command: handleRename,
      disabled: isDefaultColumn || isLocked,
    },
    {
      label: 'Duplicate',
      icon: 'pi pi-copy',
      command: handleDuplicate,
      disabled: false,
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: handleDelete,
      disabled: isDefaultColumn || isLocked,
    },
  ];

  return (
    <>
      <div ref={handleRef} className="column-header p-3" style={{ cursor: isLocked ? 'not-allowed' : 'grab' }}>
        <div className="column-header-content">
          <div className="column-title-section">
            <h3 className="column-title mb-0">
              <span>{formatStageName(column.task_stage_name)}</span>
              {isLocked && (
                <span className="lock-icon d-flex align-items-center" title="This stage position is locked">
                  <i className="pi pi-lock" />
                </span>
              )}
            </h3>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="column-badge">{totalItems}</span>

            <div
              className="d-flex border border-1 rounded justify-content-center p-2 task-lane-header-options pointer"
              onClick={() => openAddTask(column.task_stage_id)}
              title="Add task to this stage"
            >
              <i className="icon-add" />
            </div>

            <div className="pointer">
              <div
                className="d-flex border border-1 rounded justify-content-center p-2 task-lane-header-options pointer"
                onClick={(e) => opanel?.current?.toggle(e)}
              >
                <i className="pi pi-ellipsis-v d-flex align-items-center" />
              </div>
            </div>
          </div>
        </div>

        <Menu model={menuItems} popup ref={opanel} id="popup_menu" />
      </div>

      {showRenameDialog && (
        <RenameColumnDialog
          visible
          onHide={() => setShowRenameDialog(false)}
          onSubmit={confirmRename}
          currentName={column?.task_stage_name}
        />
      )}

      {showManageColumn && <ManageColumn column={column} onHide={() => setShowManageColumn(false)} />}
    </>
  );
};

export default React.memo(ColumnHeader);
